/**
 * v2 engine. Identical wiring to the v1 engine, but uses the deck-aware
 * `createYesNoQuestion` so YesNo cards participate in the controller's
 * swipe-coordination flow. All other rules are reused as-is.
 */
import { match } from "ts-pattern";

import {
  connectBankCompletedRule,
  connectBankRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/ConnectBankAccountAction";
import {
  connectPosCompletedRule,
  connectPosRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/ConnectPosAction";
import {
  connectQuickbooksCompletedRule,
  connectQuickbooksRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/ConnectQuickbooksAction";
import {
  inviteLandlordCompletedRule,
  inviteLandlordRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/InviteLandlordAction";
import {
  maxBudgetCompletedRule,
  maxBudgetRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/MaxBudgetAction";
import {
  reviewCompsCompletedRule,
  reviewCompsRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/comps/ReviewCompsAction";
import {
  scheduleCallCompletedRule,
  scheduleCallRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/ScheduleCallAction";
import {
  composeTtwCompletedRule,
  composeTtwRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/compose/ComposeTtwAction";
import {
  ttwPageCompletedRule,
  ttwPageRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/ttw/TtwPageAction";
import { confirmOperatingLocationsRule } from "@/web/routes/_protected/smb/-dashboard/-actions/ConfirmOperatingLocationsAction";
import { explorePropertiesRule } from "@/web/routes/_protected/smb/-dashboard/-actions/ExplorePropertiesAction";
import { selectPropertyCompletedRule } from "@/web/routes/_protected/smb/-dashboard/-actions/SelectPropertyCompletedRule";
import {
  uploadLeaseDocCompletedRule,
  uploadLeaseDocRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/UploadLeaseDocAction";
import {
  shareBusinessCardCompletedRule,
  shareBusinessCardRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/share/ShareBusinessCardAction";
import {
  uploadTaxReturnCompletedRule,
  uploadTaxReturnRule,
} from "@/web/routes/_protected/smb/-dashboard/-actions/UploadTaxReturnAction";
import { compsUnavailableRule } from "@/web/routes/_protected/smb/-dashboard/-loadingActions/CompsUnavailableCard";
import {
  CardRulesEngine,
  type CardRulesData,
  type Predicate,
  type UWQuestion,
} from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createUwQuestionCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/UwQuestionCompleted";
import { loadingCompsRule } from "@/web/routes/_protected/smb/-dashboard/-loadingActions/LoadingCompsCard";
import { loadingUnderwritingRule } from "@/web/routes/_protected/smb/-dashboard/-loadingActions/LoadingUnderwriting";
import { underwritingUnavailableRule } from "@/web/routes/_protected/smb/-dashboard/-loadingActions/UnderwritingUnavailableCard";
import { createFreeformTextQuestion } from "@/web/routes/_protected/smb/-dashboard/-questions/FreeformTextQuestion";
import { createMultipleChoiceQuestion } from "@/web/routes/_protected/smb/-dashboard/-questions/MultipleChoiceQuestion";
import { createNumberQuestion } from "@/web/routes/_protected/smb/-dashboard/-questions/NumberQuestion";
import { createYesNoQuestion } from "@/web/routes/_protected/smb/-dashboard/-questions/YesNoQuestion";
import { intendedContributionRule } from "@/web/routes/_protected/smb/-dashboard/-actions/IntendedContributionAction";
import { createNeedsMoreInfoQuestion } from "@/web/routes/_protected/smb/-dashboard/-questions/NeedsMoreInfoQuestion";
import { tableQueryOptions } from "@/web/lib/tanstack";
import { tables } from "@tycho/web-bindings";

/**
 * Strict parser for Athena's question identifier, `smb-{smb_id}/{question-slug}`.
 * Returns the segment after `/`, or `null` unless the id starts with `smb-` AND
 * contains exactly one `/`. We deliberately do NOT match by card title or
 * question text — only this stable id format.
 */
export function parseAthenaQuestionSlug(id: string): string | null {
  if (!id.startsWith("smb-")) return null;
  const firstSlash = id.indexOf("/");
  if (firstSlash === -1) return null;
  const secondSlash = id.indexOf("/", firstSlash + 1);
  if (secondSlash !== -1) return null;
  return id.slice(firstSlash + 1);
}

/**
 * Athena question slugs that an invited brand must NOT answer. Matches the
 * two confirmed production questions:
 * - "Do you occupy the property?" → `occupy-property`
 * - "How do you plan to use ownership capital?" → `growth-plan`
 */
const INVITED_EXCLUDED_QUESTION_SLUGS = new Set(["occupy-property", "growth-plan"]);

/**
 * Question-level predicate: true when the brand is `INVITED` AND the question's
 * parsed Athena slug is in the invited-exclusion set. Composed into both the
 * active and completed question rules.
 */
const invitedQuestionExcluded: Predicate<UWQuestion> = (d, q) => {
  if (d.businessStory.tag !== "Invited") return false;
  const slug = parseAthenaQuestionSlug(q.value.id);
  return slug !== null && INVITED_EXCLUDED_QUESTION_SLUGS.has(slug);
};

const uwQuestionsRule = CardRulesEngine.forEachRule({
  id: "uw-questions",
  priority: 55,
  forEach: CardRulesEngine.latestQuestion,
  when: CardRulesEngine.and(
    CardRulesEngine.isUnansweredQuestion,
    CardRulesEngine.not(invitedQuestionExcluded),
  ),
  build: (_, q) =>
    match(q)
      .with({ tag: "YesNo" }, (v) =>
        createYesNoQuestion({
          questionId: q.value.id,
          badge: "Budget",
          question: v.value.title,
          subtitle: v.value.subtitle,
          details: v.value.details,
        }),
      )
      .with({ tag: "Number" }, (v) =>
        createNumberQuestion({
          questionId: q.value.id,
          badge: "Budget",
          question: v.value.title,
          subtitle: v.value.subtitle,
          details: v.value.details,
          min: v.value.min,
          max: v.value.max,
          step: v.value.step,
          defaultValue: v.value.defaultValue,
          units: v.value.units.tag,
        }),
      )
      .with({ tag: "MultipleChoice" }, (v) =>
        createMultipleChoiceQuestion({
          questionId: q.value.id,
          badge: "Budget",
          question: v.value.title,
          subtitle: v.value.subtitle,
          details: v.value.details,
          options: v.value.options,
          allowMultiple: v.value.multipleChoiceType.tag === "Multi",
        }),
      )
      .with({ tag: "Freeform" }, (v) =>
        createFreeformTextQuestion({
          questionId: q.value.id,
          badge: "Budget",
          question: v.value.title,
          subtitle: v.value.subtitle,
          details: v.value.details,
        }),
      )
      .with({ tag: "NeedsMoreInfo" }, (v) =>
        createNeedsMoreInfoQuestion({
          questionId: q.value.id,
          badge: "Budget",
          question: v.value.title,
          subtitle: v.value.subtitle,
          details: v.value.details,
        }),
      )
      .exhaustive(),
});

const uwQuestionsCompletedRule = CardRulesEngine.forEachCompletedRule({
  id: "uw-questions:done",
  forEach: CardRulesEngine.latestQuestion,
  when: CardRulesEngine.and(
    CardRulesEngine.isAnsweredQuestion,
    CardRulesEngine.not(invitedQuestionExcluded),
  ),
  build: (_, q) => createUwQuestionCompleted({ question: q }),
});

const uncompletedRules = [
  shareBusinessCardRule,
  composeTtwRule,
  ttwPageRule,
  confirmOperatingLocationsRule,
  explorePropertiesRule,
  reviewCompsRule,
  maxBudgetRule,
  inviteLandlordRule,
  connectBankRule,
  connectQuickbooksRule,
  connectPosRule,
  uploadTaxReturnRule,
  uploadLeaseDocRule,
  uwQuestionsRule,
  loadingUnderwritingRule,
  underwritingUnavailableRule,
  loadingCompsRule,
  compsUnavailableRule,
  scheduleCallRule,
  intendedContributionRule,
];

const completedRules = [
  selectPropertyCompletedRule,
  reviewCompsCompletedRule,
  maxBudgetCompletedRule,
  inviteLandlordCompletedRule,
  connectBankCompletedRule,
  connectQuickbooksCompletedRule,
  connectPosCompletedRule,
  uploadTaxReturnCompletedRule,
  uploadLeaseDocCompletedRule,
  uwQuestionsCompletedRule,
  shareBusinessCardCompletedRule,
  composeTtwCompletedRule,
  ttwPageCompletedRule,
  scheduleCallCompletedRule,
];

/**
 * Feature flags that gate which rules run. Off by default (see
 * `useFeatureFlag` — absent flags read `false`), so a gated rule is excluded
 * until the flag is turned on.
 */
export type CardRulesFeatureFlags = {
  /** LLM_TTW_COMPOSER — gates the "compose your investor page" (LLM) cards. */
  llmTtwComposer: boolean;
  /** TTW_COMPOSER — gates the "design your investor page" (classical) cards. */
  ttwComposer: boolean;
};

export const cardRulesEngine = (data: CardRulesData, flags: CardRulesFeatureFlags) => {
  // Drop each composer's rules unless its flag is on.
  const uncompleted = uncompletedRules.filter(
    (r) =>
      (r !== composeTtwRule || flags.llmTtwComposer) && (r !== ttwPageRule || flags.ttwComposer),
  );
  const completed = completedRules.filter(
    (r) =>
      (r !== composeTtwCompletedRule || flags.llmTtwComposer) &&
      (r !== ttwPageCompletedRule || flags.ttwComposer),
  );

  return CardRulesEngine.run(
    // Sort properties deterministically (by id — a UUIDv7, so this is stable
    // insertion order) before any rule reads `d.properties`. The server already
    // emits them sorted; this is a belt-and-suspenders guard so a re-ordered
    // subscription payload can't churn per-property card/drawer order and
    // trigger jarring re-renders. Cheap: runs once per engine input change.
    { ...data, properties: [...data.properties].sort((a, b) => a.id.compareTo(b.id)) },
    { uncompleted, completed },
  );
};

export const cardEngineInputsOptions = tableQueryOptions(tables.cardEngineInputs);
